/**
 * Pricing catalogue — the ONE place the plans, prices and feature lines live.
 *
 * Every line here is backed by a server-side gate listed in
 * PRICING_ENFORCEMENT_AUDIT.md ("no vapor"): if a claim is not enforced in
 * backend/server.py it does not belong on this page. Tier keys match the
 * backend's `subscription_tier` values; Founding is NOT a tier — it is a
 * lifetime flag (`/subscription/status.founding`) that reads as Studio.
 *
 * Prices are KES, monthly (no annual plans exist on Paystack). Plan ids are
 * what `/payments/paystack/initialize` and `/payments/mpesa/charge` accept.
 */

export const TIER_ORDER = ['spark', 'indie', 'studio'];
export const PAID_TIERS = ['indie', 'studio'];
export const FOUNDING_CAP = 250;

/** True for any tier that carries the Indie+ gates (script editor, web, …). */
export const isPaidTier = (tier) => PAID_TIERS.includes(tier);
/** True for the Studio-only gates (Series Builder, modular exports). */
export const isStudioTier = (tier) => tier === 'studio';

export const TIER_NAMES = { spark: 'Spark', indie: 'Indie', studio: 'Studio', founding: 'Founding' };

export const PLANS = {
  spark: {
    key: 'spark',
    name: 'Spark',
    tagline: 'Your first story, free.',
    priceKES: 0,
    planId: null,
    features: [
      '1 plot or story project',
      'Unlimited characters — all 5 levels',
      'World Building & scenes: 3 items each',
      'Plot Mentor: 3 sessions a month',
      'AI Mentor on your first character (10 a month)',
      'Story Bible export (watermarked)',
      'English',
    ],
    locked: [
      'Script editor — write, import, export',
      'Character Web · Scene Builder',
      'Enneagram · Conflict Gauge · Story Auditor',
      'All 6 languages',
      'Series Builder',
    ],
  },
  indie: {
    key: 'indie',
    name: 'Indie',
    tagline: 'For the independent filmmaker.',
    priceKES: 499,
    planId: 'indie_monthly',
    features: [
      'Up to 3 plot or story projects',
      'Unlimited characters — all 5 levels',
      'Script editor — write, import & export',
      'AI Mentor: 300 generations a month (mentor, notes, auditor)',
      'Unlimited Plot Mentor',
      'Character Web · Scene Builder',
      'Enneagram · Conflict Gauge · Story Auditor',
      'All 6 languages',
      'Clean Story Bible export — no watermark',
      'Usage shown in-app',
    ],
    locked: [
      'Series Builder',
      'Modular exports by section',
    ],
  },
  studio: {
    key: 'studio',
    name: 'Studio',
    tagline: "For the writer's room.",
    priceKES: 1299,
    planId: 'studio_monthly',
    features: [
      'Unlimited projects',
      'Everything in Indie',
      'Unlimited AI Mentor',
      'Series Builder — series bible, season arcs, episode beats',
      'Modular exports — pick the sections you need',
      'Priority support',
    ],
    locked: [],
  },
  founding: {
    key: 'founding',
    name: 'Founding',
    tagline: 'Studio, for life. 250 seats.',
    priceKES: 4999,
    planId: 'founding',
    oneTime: true,
    features: [
      'Permanent Studio — never expires',
      'Everything in Studio, for life',
      'One payment. No renewals, ever.',
      `Only ${FOUNDING_CAP} seats`,
      'One seat per account',
    ],
    locked: [],
  },
};

export const PLAN_LIST = [PLANS.spark, PLANS.indie, PLANS.studio, PLANS.founding];

/**
 * Dual-currency price table — MIRRORS backend CURRENCY_CATALOG (the backend
 * is authoritative at charge time; this table only renders). Minor units
 * (×100 for both). Adding a currency = one entry here + backend config.
 * Clean local numbers, not exact FX conversions.
 */
export const SUPPORTED_CURRENCIES = ['KES', 'USD'];
export const DEFAULT_CURRENCY = 'KES';
export const CURRENCY_PRICES = {
  KES: { indie_monthly: 49900, studio_monthly: 129900, founding: 499900 },
  USD: { indie_monthly: 399, studio_monthly: 999, founding: 3800 },
};

/** Format a minor-unit amount in a currency: KES 1,299 · $9.99 */
export const formatMoney = (currency, minor) => {
  const major = minor / 100;
  if (currency === 'USD') {
    return `$${major.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return `KES ${major.toLocaleString('en-KE')}`;
};

/** Plan price in a currency (minor units); null for free plans. */
export const planPriceMinor = (currency, planId) =>
  planId ? CURRENCY_PRICES[currency]?.[planId] ?? null : null;

/**
 * Default currency GUESS from device signals — Kenya defaults KES, everyone
 * else USD. This is only the toggle's initial position: the buyer always
 * sees and can switch the currency before checkout; we never charge on the
 * guess alone (the confirmed choice is what the checkout POST carries).
 */
export const detectDefaultCurrency = () => {
  try {
    const stored = localStorage.getItem('pangaji_currency');
    if (SUPPORTED_CURRENCIES.includes(stored)) return stored;
  } catch { /* storage blocked */ }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz === 'Africa/Nairobi') return 'KES';
    const langs = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
    if (langs.some((l) => /-KE$/i.test(l) || /^sw\b/i.test(l))) return 'KES';
  } catch { /* SSR/test env */ }
  return 'USD';
};

export const rememberCurrency = (currency) => {
  try {
    localStorage.setItem('pangaji_currency', currency);
  } catch { /* storage blocked — choice just won't persist */ }
};

/** Human price labels keyed by plan id (Settings + checkout buttons). */
export const PRICE_LABELS = {
  indie_monthly: 'KES 499/mo',
  studio_monthly: 'KES 1,299/mo',
  founding: 'KES 4,999 once',
};

export const priceLabelFor = (currency, planId) => {
  const minor = planPriceMinor(currency, planId);
  if (minor == null) return '';
  const base = formatMoney(currency, minor);
  return planId === 'founding' ? `${base} once` : `${base}/mo`;
};

/** Ceiling for a rendered price: KES/USD 1,000,000 in minor units. */
const MAX_PLAUSIBLE_MINOR = 100_000_000;

/**
 * The price line on the billing card: what THIS member was actually charged,
 * from /subscription/status (`amount` in minor units + the charged
 * `currency`) — a $38 Founding member must not read "KES 4,999 once".
 * Anything the endpoint can't answer for (a free tier's 0, a legacy grant,
 * an unknown currency) falls back to the list label, so the card never goes
 * blank and never shows a figure in a currency we can't format.
 */
export const chargedPriceLabel = (subscription, planId) => {
  // Never invent a label for a plan we don't know — the pre-existing card
  // rendered nothing for an unrecognised key and it must keep doing that.
  if (!(planId in PRICE_LABELS)) return '';
  const minor = subscription?.amount;
  const currency = subscription?.currency;
  // Whole minor units only, inside a sane range, in a currency we can format.
  // Anything else is corrupt or unanswerable — show the list label instead of
  // a figure we'd be making up ("KES 0.005", "$10,000,000,000,000,000,000").
  if (!Number.isInteger(minor) || minor <= 0 || minor > MAX_PLAUSIBLE_MINOR
      || !SUPPORTED_CURRENCIES.includes(currency)) {
    return PRICE_LABELS[planId];
  }
  const base = formatMoney(currency, minor);
  return planId === 'founding' ? `${base} once` : `${base}/mo`;
};

export const formatKES = (n) => `KES ${Number(n).toLocaleString('en-KE')}`;

/** "N of 250 claimed" — always the claimed count, never remaining. */
export const foundingClaimedLabel = (claimed, cap = FOUNDING_CAP) =>
  `${Number(claimed || 0).toLocaleString('en-KE')} of ${cap} claimed`;
